import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, AlertCircle, ArrowRight, Package } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/core/auth/useAuth';

interface TransferVerificationBannerProps {
  onDismiss?: () => void;
}

const TransferVerificationBanner: React.FC<TransferVerificationBannerProps> = ({ onDismiss }) => {
  const [pendingCount, setPendingCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const { hasRole } = useAuth();

  // Check if user has shop-related permissions
  const canVerifyTransfers = hasRole('admin') || hasRole('amministrazione');

  useEffect(() => {
    if (!canVerifyTransfers) return;

    const checkPendingTransfers = async () => {
      try {
        // Get today's date for checking yesterday's transfers
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().split('T')[0];

        const { data, error } = await supabase
          .from('laundry_nets')
          .select('id')
          .eq('transfer', true)
          .eq('data_transfer', yesterdayStr)
          .eq('stato', 'attiva')
          .or('transfer_verification_status.eq.pending,transfer_verification_status.is.null');

        if (!error && data) {
          setPendingCount(data.length);
          // Always show if there are pending transfers, regardless of dismissal
          setIsVisible(data.length > 0);
        }
      } catch (error) {
        console.error('Error checking pending transfers:', error);
      }
    };

    // Check on mount
    checkPendingTransfers();

    // Set up real-time subscription
    const subscription = supabase
      .channel('transfer-verifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'laundry_nets',
          filter: 'transfer=eq.true'
        },
        () => {
          checkPendingTransfers();
        }
      )
      .subscribe();

    // Check every minute in case date changes
    const interval = setInterval(checkPendingTransfers, 60000);

    return () => {
      subscription.unsubscribe();
      clearInterval(interval);
    };
  }, [canVerifyTransfers]);

  const handleDismiss = () => {
    // Do nothing - banner should always show when there are pending transfers
    // It will only disappear when all transfers are verified
    onDismiss?.();
  };

  if (!isVisible || !canVerifyTransfers || pendingCount === 0) {
    return null;
  }

  return (
    <div className="relative w-full bg-gradient-to-r from-admin-orange via-admin-orange to-admin-orange-dark shadow-lg">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between py-2">
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-white/20 rounded-lg">
              <AlertCircle className="h-5 w-5 text-white" />
            </div>
            <div className="flex items-center gap-2 text-white">
              <span className="text-sm font-medium">
                Verifica Transfer Richiesta
              </span>
              <span className="text-sm opacity-90">
                Ci sono <span className="font-bold">{pendingCount}</span> reti da verificare per i transfer di ieri
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Link to="/shop/transfer-verification">
              <Button
                size="sm"
                variant="secondary"
                className="bg-white text-admin-orange hover:bg-gray-100 font-medium shadow-sm"
              >
                <Package className="h-4 w-4 mr-1" />
                Verifica Ora
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransferVerificationBanner;